import { t } from '@lingui/macro';
import startsWith from 'lodash/startsWith';

export enum AppFileTypes {
  _, // Ignoring first value. This is 0 and can be confusing when dealing with conditions as 0 is falsy
  PDF_DOCUMENT,
  IMAGE,
  VIDEO,
  AUDIO,
  FONT,
  WORD_DOCUMENT,
  EXCEL_DOCUMENT,
  POWERPOINT_DOCUMENT,
  ZIP,
  OTHER,
}

const AppFileTypesMatcher = [
  {
    type: AppFileTypes.VIDEO,
    matcher: (mimeType: string) => startsWith(mimeType, 'video'),
  },
  {
    type: AppFileTypes.AUDIO,
    matcher: (mimeType: string) => startsWith(mimeType, 'audio'),
  },
  {
    type: AppFileTypes.IMAGE,
    matcher: (mimeType: string) => startsWith(mimeType, 'image'),
  },
  {
    type: AppFileTypes.PDF_DOCUMENT,
    matcher: (mimeType: string) => mimeType === 'application/pdf',
  },
  {
    type: AppFileTypes.FONT,
    matcher: (mimeType: string) => startsWith(mimeType, 'font'),
  },
  {
    type: AppFileTypes.ZIP,
    matcher: (mimeType: string) =>
      ['zip', 'application/zip'].includes(mimeType),
  },
  {
    type: AppFileTypes.WORD_DOCUMENT,
    matcher: (mimeType: string) =>
      [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
        'application/vnd.ms-word.document.macroEnabled.12',
        'application/vnd.ms-word.template.macroEnabled.12',
      ].includes(mimeType),
  },
  {
    type: AppFileTypes.EXCEL_DOCUMENT,
    matcher: (mimeType: string) =>
      [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.template.macroEnabled.12',
        'application/vnd.ms-excel.addin.macroEnabled.12',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
      ].includes(mimeType),
  },
  {
    type: AppFileTypes.POWERPOINT_DOCUMENT,
    matcher: (mimeType: string) =>
      [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.openxmlformats-officedocument.presentationml.template',
        'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
        'application/vnd.ms-powerpoint.addin.macroEnabled.12',
        'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
        'application/vnd.ms-powerpoint.template.macroEnabled.12',
        'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
      ].includes(mimeType),
  },
];

export function mimeTypeToAppType(mimeType?: string): AppFileTypes {
  if (!mimeType) {
    return AppFileTypes.OTHER;
  }

  return (
    AppFileTypesMatcher.find(({ matcher }) => matcher(mimeType))?.type ||
    AppFileTypes.OTHER
  );
}

export function mimeTypeToUserFriendlyString(mimeType?: string): string {
  if (!mimeType) {
    return t`Unknown file type`;
  }

  switch (mimeTypeToAppType(mimeType)) {
    case AppFileTypes.AUDIO:
      return t`Audio`;
    case AppFileTypes.VIDEO:
      return t`Video`;
    case AppFileTypes.FONT:
      return t`Font`;
    case AppFileTypes.PDF_DOCUMENT:
      return t`PDF Document`;
    case AppFileTypes.IMAGE:
      return t`Image`;
    case AppFileTypes.EXCEL_DOCUMENT:
      return t`MS Excel Worksheet`;
    case AppFileTypes.POWERPOINT_DOCUMENT:
      return t`MS PowerPoint Presentation`;
    case AppFileTypes.WORD_DOCUMENT:
      return t`MS Word Document`;
    case AppFileTypes.ZIP:
      return t`ZIP Archive`;
  }

  const mimeParts = mimeType.split('/');
  const subType = mimeParts[1];
  const derivedFileType = subType?.toUpperCase() || mimeType.toUpperCase();
  return t`${derivedFileType} File`;
}

export function mimeTypeToShortString(mimeType?: string): string {
  if (!mimeType) {
    return t`Unknown`;
  }

  switch (mimeTypeToAppType(mimeType)) {
    case AppFileTypes.AUDIO:
      return t`Audio`;
    case AppFileTypes.VIDEO:
      return t`Video`;
    case AppFileTypes.FONT:
      return t`Font`;
    case AppFileTypes.PDF_DOCUMENT:
      return t`PDF`;
    case AppFileTypes.IMAGE:
      return t`Image`;
    case AppFileTypes.EXCEL_DOCUMENT:
      return t`MS Excel`;
    case AppFileTypes.POWERPOINT_DOCUMENT:
      return t`MS PowerPoint`;
    case AppFileTypes.WORD_DOCUMENT:
      return t`MS Word`;
    case AppFileTypes.ZIP:
      return t`ZIP `;
  }

  const mimeParts = mimeType.split('/');
  const subType = mimeParts[1];
  const derivedFileType = subType?.toUpperCase() || mimeType.toUpperCase();
  return derivedFileType;
}
